import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Card, CardContent } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Input, FormField } from '../../../../components/ui/FormField';
import { Money } from '../../../../components/ui/Money';
import { useToast } from '../../../../components/ui/Toast';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../../components/ui/Dialog';
import { 
  useReceiptVouchers, 
  useCancelReceiptVoucher, 
  type ReceiptVoucher 
} from '../../../../features/finance/hooks';
import { 
  Search, 
  Plus, 
  Printer, 
  FileText, 
  AlertTriangle, 
  ArrowUpRight, 
  Clock, 
  BookOpen 
} from 'lucide-react';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { ReceiptVoucherGrid } from '../ReceiptVoucherGrid';
import { usePageTitle } from '../../../../lib/hooks';

export const Route = createFileRoute('/_auth/finance/receipt-vouchers/')({
  component: ReceiptVouchersPage,
});

const CATEGORY_NAMES: Record<string, string> = {
  membership_fees: 'Membership Fees',
  application_fees: 'Application Fees',
  interest_advances: 'Interest on Advances',
  misc_income: 'Miscellaneous Income',
  asset_sales: 'Sale of Office Assets',
  advance_refund: 'Short-term Advance Refund',
};

function amountToWords(rupees: number): string {
  if (rupees === 0) return 'Zero Rupees Only';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const scales = ['', 'Thousand', 'Lakh', 'Crore'];

  const convertLessThanThousand = (num: number): string => {
    let str = '';
    if (num >= 100) { str += ones[Math.floor(num / 100)] + ' Hundred '; num %= 100; }
    if (num >= 20) { str += tens[Math.floor(num / 10)] + ' '; num %= 10; }
    if (num > 0) { str += ones[num] + ' '; }
    return str.trim();
  };

  let result = '';
  let temp = Math.floor(rupees);
  const parts = [];
  parts.push(temp % 1000);
  temp = Math.floor(temp / 100);
  parts.push(temp > 0 ? temp % 100 : 0);
  temp = Math.floor(temp / 100);
  parts.push(temp > 0 ? temp % 100 : 0);
  temp = Math.floor(temp / 100);
  if (temp > 0) parts.push(temp);

  for (let i = parts.length - 1; i >= 0; i--) {
    const val = parts[i];
    if (val > 0) {
      const scaleName = scales[i];
      result += (i === 0 ? convertLessThanThousand(val) : convertLessThanThousand(val) + ' ' + scaleName) + ' ';
    }
  }
  return result.trim() + ' Rupees Only';
}

function ReceiptVouchersPage() {
  usePageTitle('Receipt Vouchers');
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [query, setQuery] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [modeFilter, setModeFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  
  const receiptvouchersQuery = useReceiptVouchers({ query, category: categoryFilter, paymentMode: modeFilter, status: statusFilter });
  const { data: response, isLoading, isError, error, refetch } = receiptvouchersQuery;
  const vouchers = response?.data;

  const cancelMutation = useCancelReceiptVoucher();
  const [isCancelOpen, setIsCancelOpen] = React.useState(false);
  const [isPrintOpen, setIsPrintOpen] = React.useState(false);
  const [selectedVoucher, setSelectedVoucher] = React.useState<ReceiptVoucher | null>(null);
  const [cancelReason, setCancelReason] = React.useState('');
  const [cancelError, setCancelError] = React.useState('');

  const handleResetFilters = () => { setQuery(''); setCategoryFilter(''); setModeFilter(''); setStatusFilter(''); };

  const handleCancelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVoucher) return;
    if (!cancelReason.trim() || cancelReason.trim().length < 20) {
      setCancelError('Reason must be at least 20 characters long per governance rules');
      return;
    }
    cancelMutation.mutate({ id: selectedVoucher.id, reason: cancelReason.trim() }, {
      onSuccess: (data) => {
        toast({ type: 'success', title: 'Voucher Cancelled', description: `Voucher ${data.voucherNumber} has been successfully cancelled and ledger transactions reversed.` });
        setIsCancelOpen(false); setCancelReason(''); setCancelError(''); setSelectedVoucher(null);
      },
      onError: (err: { message?: string }) => {
        toast({ type: 'error', title: 'Failed to Cancel Voucher', description: err?.message || 'Voucher cancellation failed. The voucher may have been modified. Refresh and try again.' });
      }
    });
  };

  const handlePrintAction = (v: ReceiptVoucher) => { setSelectedVoucher(v); setIsPrintOpen(true); };
  const handleCancelAction = (v: ReceiptVoucher) => { setSelectedVoucher(v); setIsCancelOpen(true); };

  const handleExportCSV = () => {
    if (!vouchers || vouchers.length === 0) {
      toast({ type: 'error', title: 'Export Failed', description: 'No vouchers to export.' });
      return;
    }
    const headers = ['Voucher No', 'Date', 'Depositor', 'Category', 'Mode', 'Amount', 'Status'];
    const csvContent = [
      headers.join(','),
      ...vouchers.map(v => [
        v.voucherNumber,
        new Date(v.voucherDate).toLocaleDateString(),
        `"${v.depositorName}"`,
        v.category || '',
        v.paymentMode,
        (v.amountPaise / 100).toFixed(2),
        v.status
      ].join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `receipt_vouchers_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeVouchers = (vouchers || []).filter(v => v.status === 'APPROVED');
  const totalVolumePaise = activeVouchers.reduce((acc, v) => acc + v.amountPaise, 0);
  const today = new Date().toDateString();
  const todayCashPaise = activeVouchers.filter(v => v.paymentMode === 'CASH' && new Date(v.voucherDate).toDateString() === today).reduce((acc, v) => acc + v.amountPaise, 0);
  const todayDigitalPaise = activeVouchers.filter(v => v.paymentMode !== 'CASH' && new Date(v.voucherDate).toDateString() === today).reduce((acc, v) => acc + v.amountPaise, 0);
  const cancelledCount = vouchers?.filter(v => v.status === 'CANCELLED').length;

  const toolbar = (
    <div className="flex flex-wrap gap-4 items-center bg-surface-1 border border-border p-4 rounded-xl shadow-sm">
      <div className="relative max-w-sm flex-1">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
        <Input
          type="text"
          placeholder="Search depositor, voucher no, narration..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="w-full h-9 pl-9 pr-3"
        />
      </div>
      <div className="flex gap-2 flex-wrap">
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="h-9 px-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500">
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_NAMES).map(([key, value]) => (<option key={key} value={key}>{value}</option>))}
        </select>
        <select value={modeFilter} onChange={e => setModeFilter(e.target.value)}
          className="h-9 px-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500">
          <option value="">All Payment Modes</option>
          <option value="CASH">Cash</option><option value="UPI">UPI / Online</option><option value="NEFT">Bank Transfer / NEFT</option><option value="CHEQUE">Cheque</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="h-9 px-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500">
          <option value="">All Statuses</option><option value="APPROVED">Active</option><option value="CANCELLED">Cancelled</option>
        </select>
        {(query || categoryFilter || modeFilter || statusFilter) && (
          <Button variant="ghost" size="sm" onClick={handleResetFilters} className="text-text-muted">Reset</Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Receipt Vouchers"
        subtitle="Manage non-subscription income and miscellaneous receipt vouchers."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleExportCSV}>
              <FileText className="w-4 h-4 mr-2" />Export CSV
            </Button>
            <Button variant="primary" onClick={() => navigate({ to: '/finance/receipt-vouchers/new' })}>
              <Plus className="w-4 h-4 mr-2" />Record Receipt Voucher
            </Button>
          </div>
        }
      />
      {toolbar}

      {isLoading ? (
        <Skeleton rows={8} />
      ) : isError ? (
        <div className="p-8 text-center bg-surface-1 border border-border rounded-xl">
          <p className="text-sm font-semibold text-danger-600">Failed to load receipt vouchers</p>
          <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="shadow-sm border border-border">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="space-y-1">
                  <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Total Month Receipts</span>
                  <h2 className="text-xl font-bold font-mono text-text-primary"><Money paise={totalVolumePaise} /></h2>
                </div>
                <div className="p-2.5 bg-accent-500/10 text-accent-500 rounded-lg"><BookOpen className="w-5 h-5" /></div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border border-border">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="space-y-1">
                  <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Today's Cash Receipts</span>
                  <h2 className="text-xl font-bold font-mono text-success-text"><Money paise={todayCashPaise} /></h2>
                </div>
                <div className="p-2.5 bg-success-500/10 text-success-text rounded-lg"><ArrowUpRight className="w-5 h-5" /></div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border border-border">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="space-y-1">
                  <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Today's Digital Receipts</span>
                  <h2 className="text-xl font-bold font-mono text-info-text"><Money paise={todayDigitalPaise} /></h2>
                </div>
                <div className="p-2.5 bg-info-500/10 text-info-text rounded-lg"><Clock className="w-5 h-5" /></div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border border-border">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="space-y-1">
                  <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Cancelled Vouchers</span>
                  <h2 className="text-xl font-bold font-mono text-danger-text">{cancelledCount} <span className="text-xs font-normal text-text-muted">Vouchers</span></h2>
                </div>
                <div className="p-2.5 bg-danger-500/10 text-danger-text rounded-lg"><AlertTriangle className="w-5 h-5" /></div>
              </CardContent>
            </Card>
          </div>
          {vouchers?.length === 0 ? (
            <div className="bg-surface-1 border border-border rounded-xl p-8 shadow-sm">
              <EmptyState
                title="No Receipt Vouchers Found"
                description={query || categoryFilter || modeFilter || statusFilter ? 'No miscellaneous vouchers match your current filters. Try resetting the filters.' : 'There are no standalone miscellaneous receipts recorded in the cash book yet.'}
                variant={query || categoryFilter || modeFilter || statusFilter ? 'filtered' : 'no-data'}
                action={query || categoryFilter || modeFilter || statusFilter ? { label: 'Clear Filters', onClick: handleResetFilters } : { label: 'Record First Voucher', onClick: () => navigate({ to: '/finance/receipt-vouchers/new' })}}
              />
            </div>
          ) : (
            <ReceiptVoucherGrid vouchers={vouchers} onPrint={handlePrintAction} onCancel={handleCancelAction} categoryNames={CATEGORY_NAMES} />
          )}
        </>
      )}

      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-danger-text flex items-center gap-1.5">
              <AlertTriangle className="w-5 h-5 text-danger-text animate-pulse" />
              Revert / Cancel Receipt Voucher
            </DialogTitle>
            <DialogDescription>This is a permanent auditing reversal. Soft delete is tracked. Enter a valid regulatory reason (min 20 chars).</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCancelSubmit} className="space-y-4 py-2">
            <div className="bg-surface-2 p-3.5 rounded-lg border border-border space-y-2">
              <div className="flex justify-between text-xs font-semibold text-text-secondary">
                <span>VOUCHER NO:</span><span className="font-mono text-text-primary">{selectedVoucher?.voucherNumber}</span>
              </div>
              <div className="flex justify-between text-xs font-semibold text-text-secondary">
                <span>DEPOSITOR:</span><span className="text-text-primary">{selectedVoucher?.depositorName}</span>
              </div>
              <div className="flex justify-between text-xs font-semibold text-text-secondary">
                <span>AMOUNT:</span><span className="font-mono text-text-primary"><Money paise={selectedVoucher?.amountPaise || 0} /></span>
              </div>
            </div>
            <FormField label="Cancellation & Auditing Reason" required error={cancelError}>
              <textarea
                className="w-full min-h-[80px] p-2 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 placeholder:text-text-muted focus:ring-1 focus:ring-accent-500"
                placeholder="Describe reason for cancellation..."
                value={cancelReason}
                onChange={e => { setCancelReason(e.target.value); if (e.target.value.trim().length >= 20) setCancelError(''); }}
              />
            </FormField>
            <DialogFooter className="pt-4 border-t border-border">
              <Button type="button" variant="ghost" onClick={() => setIsCancelOpen(false)}>Go Back</Button>
              <Button type="submit" variant="danger" loading={cancelMutation.isPending}>Revert Transaction</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isPrintOpen} onOpenChange={setIsPrintOpen}>
        <DialogContent className="max-w-2xl bg-surface-1 text-text-primary p-0 overflow-hidden">
          <div className="p-8 space-y-6 max-h-[85vh] overflow-y-auto" id="printable-receipt-voucher">
            {selectedVoucher?.status === 'CANCELLED' && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-danger-bg text-danger-text">Cancelled</span>
            )}
            <div className="flex justify-between items-start border-b-2 border-border pb-5">
              <div className="space-y-1">
                <span className="text-2xl font-bold tracking-tight text-text-primary uppercase">Kraphene Finance Pvt Ltd</span>
                <p className="text-xs text-text-muted max-w-sm font-medium">Plot No. 44, Tech Hub Park, Sector 5, Madhapur, Hyderabad - 500081, Telangana, India.</p>
                <p className="text-caption font-mono font-bold text-text-muted">GSTIN: 36AAACO8217H1ZC | CIN: U65991TG2026PTC29183</p>
              </div>
              <div className="text-right space-y-1 flex flex-col items-end">
                <div className="bg-surface-2 px-3 py-1 rounded-[6px] border border-border">
                  <span className="text-xs font-bold text-text-secondary block">OFFICIAL RECEIPT VOUCHER</span>
                </div>
                <span className="font-mono text-xs text-text-muted font-semibold mt-1">No: <span className="font-bold text-text-primary">{selectedVoucher?.voucherNumber}</span></span>
                <span className="font-mono text-xs text-text-muted font-semibold">Date: <span className="font-bold text-text-primary">
                  {selectedVoucher ? new Date(selectedVoucher.voucherDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </span></span>
              </div>
            </div>
            <div className="bg-surface-2 border border-border p-4 rounded-xl space-y-4 text-sm">
              <div className="grid grid-cols-12 gap-y-3">
                <div className="col-span-3 text-text-muted font-medium">Received From:</div>
                <div className="col-span-9 font-bold text-text-primary border-b border-dashed border-border pb-0.5 uppercase tracking-wide">
                  {selectedVoucher?.depositorName}
                </div>
                <div className="col-span-3 text-text-muted font-medium">Account Credited:</div>
                <div className="col-span-9 font-semibold text-text-primary border-b border-dashed border-border pb-0.5">
                  {selectedVoucher?.category ? (CATEGORY_NAMES[selectedVoucher.category] || selectedVoucher.category) : ''} Account
                </div>
                <div className="col-span-3 text-text-muted font-medium">Narration / Notes:</div>
                <div className="col-span-9 text-text-secondary border-b border-dashed border-border pb-0.5 italic">{selectedVoucher?.notes}</div>
                <div className="col-span-3 text-text-muted font-medium">Payment Mode:</div>
                <div className="col-span-9 font-semibold text-text-primary border-b border-dashed border-border pb-0.5 flex justify-between">
                  <span>{selectedVoucher?.paymentMode}</span>
                  {selectedVoucher?.referenceNumber && <span className="font-mono text-xs font-bold text-text-muted">Ref/Inst No: {selectedVoucher.referenceNumber}</span>}
                </div>
                <div className="col-span-3 text-text-muted font-medium">Amount in Words:</div>
                <div className="col-span-9 font-semibold text-text-primary border-b border-dashed border-border pb-0.5 text-xs italic">
                  {selectedVoucher ? amountToWords(selectedVoucher.amountPaise / 100) : ''}
                </div>
              </div>
            </div>
            <div className="flex justify-between items-end pt-4 border-t border-border">
              <div className="bg-surface-3 text-white px-5 py-3 rounded-lg flex items-center gap-4 border-2 border-border shadow-md">
                <span className="text-xs uppercase tracking-wider font-bold text-text-muted">Total Credit</span>
                <span className="font-mono text-xl font-extrabold tracking-tight">{selectedVoucher ? <Money paise={selectedVoucher.amountPaise} /> : ''}</span>
              </div>
              <div className="flex gap-12 text-center text-xs font-semibold">
                <div className="space-y-12">
                  <div className="border-b border-border w-32 pb-1 font-mono text-text-muted font-bold text-caption">{selectedVoucher?.recordedBy}</div>
                  <span className="text-text-muted text-caption uppercase font-bold tracking-wider block">Authorized Signatory</span>
                </div>
                <div className="space-y-12">
                  <div className="border-b border-border w-32 pb-1 text-text-muted">.</div>
                  <span className="text-text-muted text-caption uppercase font-bold tracking-wider block">Depositor Signature</span>
                </div>
              </div>
            </div>
            <div className="text-caption text-text-muted leading-relaxed pt-4 border-t border-border">
              <strong>Notice:</strong> This is a computer-generated receipt voucher acknowledging credit onto the ledger account listed above. Subject to clearing if paid via Cheque / Demand Draft. Vouchers cancelled or reversed under accounting norms are marked void and lead to reciprocal reverse entries in parent sub-ledgers.
            </div>
          </div>
          <div className="bg-surface-2 p-4 border-t border-border flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsPrintOpen(false)} className="border-border text-text-primary hover:bg-surface-3">Close Preview</Button>
            <Button variant="primary" onClick={() => window.print()} className="bg-surface-3 hover:bg-surface-2 text-white"><Printer className="w-4 h-4 mr-2" />Print Receipt</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
